import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * AutosaveField — a text input or textarea that saves as you type
 * (T-0184).
 *
 * The prototype's side panels say "Saved to the draft as you type": there
 * is no Save button. This field keeps what is typed locally and calls
 * `onSave` with the trimmed text once typing pauses (`delayMs`), when the
 * field loses focus, on Enter in a single-line field, and when it unmounts
 * with a save still waiting. Nothing is sent when the text did not change.
 *
 * `value` is what the server holds. When it moves (the save landed, or the
 * draft was reloaded) the field adopts it, unless something typed here is
 * not saved yet. Remount the field (a new `key`) to drop local text.
 *
 * Esc puts back the last saved text without closing a side panel around
 * it. A `required` field never saves a blank value: on blur it shows the
 * saved text again.
 *
 * Styling mirrors `.input` in `docs/requirement-library/prototype/
 * tokens.css`; `workspaceFieldClass` is the same look for selects.
 */

export const AUTOSAVE_DELAY_MS = 800;

export const workspaceFieldClass =
  'block w-full rounded-card border border-ink-200 bg-paper-0 px-3 text-body text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600 disabled:cursor-not-allowed disabled:opacity-60';

export interface AutosaveFieldProps {
  value: string;
  onSave: (value: string) => void;
  'aria-label': string;
  multiline?: boolean;
  /** Visible rows of a multiline field. Default 2. */
  rows?: number;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  delayMs?: number;
  id?: string;
  className?: string;
}

export function AutosaveField({
  value,
  onSave,
  'aria-label': ariaLabel,
  multiline = false,
  rows = 2,
  placeholder,
  required = false,
  disabled = false,
  maxLength,
  delayMs = AUTOSAVE_DELAY_MS,
  id,
  className,
}: AutosaveFieldProps) {
  const [text, setText] = React.useState(value);
  const textRef = React.useRef(value);
  const savedRef = React.useRef(value);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSaveRef = React.useRef(onSave);
  onSaveRef.current = onSave;

  React.useEffect(() => {
    if (value === savedRef.current) return;
    const dirty = textRef.current.trim() !== savedRef.current.trim();
    savedRef.current = value;
    if (!dirty) {
      textRef.current = value;
      setText(value);
    }
  }, [value]);

  const commit = React.useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const next = textRef.current.trim();
    if (required && next === '') return;
    if (next === savedRef.current.trim()) return;
    savedRef.current = next;
    onSaveRef.current(next);
  }, [required]);

  // A save still waiting when the field goes away (the panel closed) is sent.
  const commitRef = React.useRef(commit);
  commitRef.current = commit;
  React.useEffect(
    () => () => {
      if (timer.current) commitRef.current();
    },
    [],
  );

  const shared = {
    id,
    'aria-label': ariaLabel,
    value: text,
    placeholder,
    disabled,
    maxLength,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      textRef.current = e.target.value;
      setText(e.target.value);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => commitRef.current(), delayMs);
    },
    onBlur: () => {
      commit();
      if (required && textRef.current.trim() === '') {
        textRef.current = savedRef.current;
        setText(savedRef.current);
      }
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (timer.current) clearTimeout(timer.current);
        timer.current = null;
        textRef.current = savedRef.current;
        setText(savedRef.current);
        e.currentTarget.blur();
      } else if (e.key === 'Enter' && !multiline) {
        e.preventDefault();
        commit();
      }
    },
  };

  return multiline ? (
    <textarea
      {...shared}
      rows={rows}
      className={cn(workspaceFieldClass, 'resize-y py-2 leading-5', className)}
    />
  ) : (
    <input {...shared} type="text" className={cn(workspaceFieldClass, 'h-9', className)} />
  );
}

AutosaveField.displayName = 'AutosaveField';
